import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { cloneDeep } from "lodash";
import { useParams, useLocation } from "react-router-dom";

import { roundOff } from "src/utils/helperFunctions";
import { QUESTIONS } from "../../constant";
// import { HIDE_QUESTION_SECTION_COMPANIES } from "src/constants/globalConstants";

import PdfReportApi from "src/services/apis/pdfReport";
import QuestionsSection from "../../QuestionsSection";
import Trait from "../Trait";
import ScrollIcon from "src/components/Common/ScrollIcon";
import { ReportPdfContext } from "src/utils/contexts";
import QuestionFilters, { EMPTY_FILTERS, describeChat, describeCoding, describeQuiz, matches } from "../Report40/QuestionFilters";

export const BucketDetails = ({
  onBack,
  displayName: name,
  description,
  score: { score, upperBound, lowerBound, median },
  traitsMeta,
  headerTraitColor = "grey",
  plagiarismConfig,
  companyId,
  audioProctoring = false,
  focusQuestionId = null,
  onQuestionFocused,
  // The web report's 4.0 detail page (V2): filters over the questions, and what goes at its foot.
  filterable = false,
  footer = null,
}) => {

  // const hideQuestionSection = HIDE_QUESTION_SECTION_COMPANIES.includes(companyId);

  const { reportPdfView } = useContext(ReportPdfContext);
  const { search } = useLocation();

  const [traitsIds, setTraitsIds] = useState(null);

  const { linkId } = useParams();
  const [mcqData, setMcqData] = useState([]);
  const [codingData, setCodingData] = useState([]);
  const [chatData, setChatData] = useState([]);
  // [AUDIO PROCTORING v2] questionId -> total speech time (ms) detected during it.
  const [speechByQuestionId, setSpeechByQuestionId] = useState({});
  // The quiz and coding lists load separately and the coding list renders above
  // the quiz list, so a linked question is scrolled to only once both are in.
  const [quizLoaded, setQuizLoaded] = useState(false);
  const [codingLoaded, setCodingLoaded] = useState(false);

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const isHideHeaderForReport = useMemo(() => params?.get("hideHeader")?.toLowerCase() === "true", [params]);
  

  // Use ref to avoid async update after setting mcq, code data 
  const traitsDataRef = useRef({});

  useEffect(() => {
    if (!traitsMeta) return;
    const newTraitsMeta = cloneDeep(traitsMeta);
    for (const key in traitsMeta) {
      newTraitsMeta[key] = { ...traitsMeta[key], userData: { userScore: 0, totalScore: 0 } };
    }
    traitsDataRef.current = { ...traitsDataRef.current, ...newTraitsMeta };
    setTraitsIds(Object.keys(traitsMeta));
  }, [traitsMeta]);

  useEffect(() => {
    if (traitsIds && !isHideHeaderForReport) {
      getQuizDetailsFromTraits();
      getCodingDetailsFromTraits();
    }
  }, [traitsIds]);

  // [AUDIO PROCTORING v2] Sum the VAD speech time detected during each question so
  // the answer breakdown can badge questions where the candidate spoke. Gated on
  // audioProctoring — no audio_events exist unless the position config enabled it,
  // so skip the call entirely when it's off.
  useEffect(() => {
    if (!linkId || !audioProctoring) return;
    (async () => {
      try {
        const response = await PdfReportApi.getAudioEvents(linkId);
        const bySpeech = {};
        (response?.events || []).forEach((event) => {
          if (!event.questionId) return;
          bySpeech[event.questionId] = (bySpeech[event.questionId] || 0) + (event.durationMs || 0);
        });
        setSpeechByQuestionId(bySpeech);
      } catch (err) { /* no speech data — badges simply don't render */ }
    })();
  }, [linkId, audioProctoring]);

  const getMcqAndChatData = (data) => {
    let mcqObj = {};
    let chatArr = [];
    const newTraitsData = cloneDeep(traitsDataRef.current);
    // For below type of question -> Extract score from traitScore key
    const traitScoreQuestionTypes = [QUESTIONS.CHAT, QUESTIONS.FREE_TEXT, QUESTIONS.SPEECH_SPONTANEOUS];
    Object.keys(data ?? {}).forEach((key) => {
      const { name, questions } = data[key];
      questions.forEach((elem) => {
        const { question, answer } = elem;
        const userScore = traitScoreQuestionTypes.includes(question.type) ?
          answer?.traitScore : answer.score?.userScore;
        const maxScore = answer.score?.maxScore;
        newTraitsData[key].userData.userScore += (userScore || 0);
        newTraitsData[key].userData.totalScore += (maxScore || 0);

        if (question.type === QUESTIONS.CHAT) {
          // Same Q can be under many traits -> Check and eliminate from spawning multiple times.
          const questionAlreadyMapped = chatArr.find(chatElem => chatElem.question.id === question.id);
          if (!questionAlreadyMapped) chatArr.push(elem);
        } else {
          if (mcqObj[question.id]) {
            mcqObj[question.id].traits.add({ name, maxScore, userScore });
          } else {
            const traits = new Set([{ name, maxScore, userScore }])
            mcqObj[question.id] = { ...elem, traits };
          }
        }
      });
    });
    traitsDataRef.current = { ...traitsDataRef.current, ...newTraitsData };
    return [
      Object.values(mcqObj)
        .map((elem) => {
          elem.answer.createdAt = new Date(elem.answer.createdAt);
          return elem
        })
        .sort((a, b) => a?.answer?.createdAt - b?.answer?.createdAt),
      chatArr
    ];
  };

  const getCodingData = (data) => {
    let dataObj = {};
    const { traits, problems } = data;
    const newTraitsData = cloneDeep(traitsDataRef.current);

    Object.keys(traits).forEach((trait) => {
      const { name, problemIds } = traits[trait];
      problemIds.forEach((elem) => {
        const userScore = problems[elem]?.score?.userScore,
          maxScore = problems[elem]?.score?.maxScore;
        newTraitsData[trait].userData.userScore += (userScore || 0);
        newTraitsData[trait].userData.totalScore += (maxScore || 0);

        if (dataObj[elem]) {
          dataObj[elem].traits.add({ name, userScore, maxScore });
        } else {
          const traits = new Set([{ name, userScore, maxScore }]);
          const problemDetails = problems[elem];
          dataObj[elem] = { ...problemDetails, traits };
        }
      })
    });
    traitsDataRef.current = { ...traitsDataRef.current, ...newTraitsData };
    return Object.values(dataObj)
      .map((elem) => {
        if (elem?.submission) {
          elem.submission.createdAt = new Date(elem.submission.createdAt);
        }
        return elem;
      })
      .sort((a, b) => a?.submission?.createdAt - b?.submission?.createdAt);
  };

  const getQuizDetailsFromTraits = async () => {
    if (traitsIds.length === 0) return;

    try {
      const response = await PdfReportApi.getQuizDetailsFromTraits(linkId, traitsIds.join(","));
      if (response?.success) {
        const [mcq, chat] = response.data ? getMcqAndChatData(response.data) : [[], []];
        setMcqData(mcq);
        setChatData(chat);
      };
    } catch (err) {
      console.error(err);
    } finally {
      setQuizLoaded(true);
    }
  };

  const getCodingDetailsFromTraits = async () => {
    if (traitsIds.length === 0) return;

    try {
      const response = await PdfReportApi.getCodingDetailsFromTraits(linkId, traitsIds.join(","));
      if (response?.success) {
        const coding = response.data ? getCodingData(response.data) : [];
        setCodingData(coding);
      };
    } catch (err) {
      console.error(err);
    } finally {
      setCodingLoaded(true);
    }
  };

  const quizItems = useMemo(() => mcqData.map(describeQuiz), [mcqData]);
  const codingItems = useMemo(() => codingData.map(describeCoding), [codingData]);
  const chatItems = useMemo(() => chatData.map(describeChat), [chatData]);
  const filterSkills = useMemo(
    () => [...new Set([...quizItems, ...codingItems].flatMap(({ skills }) => skills))],
    [quizItems, codingItems]
  );
  const shownMcq = filterable ? mcqData.filter((_, i) => matches(quizItems[i], filters)) : mcqData;
  const shownCoding = filterable ? codingData.filter((_, i) => matches(codingItems[i], filters)) : codingData;
  const shownChat = filterable ? chatData.filter((_, i) => matches(chatItems[i], filters)) : chatData;
  const showFilters = filterable && quizLoaded && codingLoaded && quizItems.length + codingItems.length + chatItems.length > 1;

  const scrollHandler = () => {
    const scrolledWindow = document.querySelector(".content-wrapper");
    if (scrolledWindow) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      scrolledWindow.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="bucket-detail-container">
      {
        !reportPdfView &&
        <div className="header--goBack" onClick={onBack}>
          <img className="icon" src="/img/right-arrow.png" alt="" />
          <span className="text">Back</span>
        </div>
      }
      <div className={`bucket-detail-header ${reportPdfView ? "pdf" : ""}`} >
        <Trait
          showInSingleRow
          separateMobileRow
          hideAverageLegends
          displayName={name}
          description={description}
          score={score}
          upperBound={upperBound}
          lowerBound={lowerBound}
          median={median}
          traitsMeta={traitsMeta}
          headerTraitColor={headerTraitColor}
          fillBlockColor={"grey"}
        />
        <div className="group-container">
          <h4>Skill wise performance</h4>
          <div className="group-container--traits" key={traitsDataRef.current}>
            {traitsDataRef.current &&
              Object.values(traitsDataRef.current)
                .filter(({ score }) => score !== null)
                .map((traitData, index, arr) => {
                  const {
                    userData: { userScore, totalScore },
                    upperBound: traitUb,
                    lowerBound: traitLb,
                    score: traitScore,
                    median: traitMedian
                  } = traitData;
                  const scoreString = totalScore ? `${roundOff(userScore)}/${roundOff(totalScore)}` : "";
                  return (
                    <Trait
                      {...traitData}
                      score={traitScore}
                      upperBound={traitUb?.score}
                      lowerBound={traitLb?.score}
                      median={traitMedian}
                      description={null}
                      scoreString={scoreString}
                      showInSingleRow
                      hideAverageLegends
                    />
                  )
                })}
          </div>
        </div>
      </div>
      { isHideHeaderForReport ? 
        null
        :
        <>
        {showFilters && (
          <QuestionFilters
            skills={filterSkills}
            items={[...quizItems, ...codingItems, ...chatItems]}
            filters={filters}
            onChange={setFilters}
          />
        )}
        <QuestionsSection
          codingData={shownCoding}
          mcqData={shownMcq}
          chatData={shownChat}
          plagiarismConfig={plagiarismConfig}
          speechByQuestionId={speechByQuestionId}
          focusQuestionId={quizLoaded && codingLoaded ? focusQuestionId : null}
          onQuestionFocused={onQuestionFocused}
        />
        </>
      }
      {footer}
      <ScrollIcon
        className={'scroll-to-top'}
        onClick={scrollHandler}
      />
    </div>
  );
};
